/**
 * **An Online Training module** (`/trainings/modules/:slug`, 2026-11-02).
 *
 * The CEO's layout: the page splits into two columns — the VIDEO on the left
 * (three quarters) with a big play button in the middle, and the module's
 * MENU on the right (one quarter): its sections, each with its topics.
 * Under the video, Previous / Next jump from topic to topic.
 *
 * The open topic is in the URL (`?topic=`), so a link, a reload and the
 * browser's Back all land on the same video. Without one the page opens
 * where the viewer left off — the first video they have not finished.
 *
 * Once the viewer has pressed play, moving to another topic keeps playing
 * (the click was the gesture browsers ask for before autoplay). An uploaded
 * video that ends offers the next one with a short countdown. Finished
 * videos get a check mark — remembered in this browser only
 * (`module-player.ts`); nothing about progress is sent anywhere.
 */
import { Box, Button, Chip, Collapse, LinearProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  ExternalLink,
  PartyPopper,
  Play,
  PlayCircle,
  RotateCcw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "../../../components/ui/back-button.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { isPermissionDenied } from "../../../lib/api.js";
import { getModule, type ModuleDetail, type ModuleSection, type ModuleTopic } from "./modules-api.js";
import {
  formatClock,
  playableSource,
  positionOf,
  progressOf,
  readWatched,
  resumeTopic,
  withAutoplay,
  writeWatched,
} from "./module-player.js";
import { TRAININGS_PATH } from "./training-paths.js";

/** Seconds the "Up next" card counts down before playing the next video. */
export const UP_NEXT_SECONDS = 5;

export function ModulePlayerPage(): JSX.Element {
  const { slug = "" } = useParams();
  const query = useQuery({
    queryKey: ["reseller", "training-module", slug],
    queryFn: () => getModule(slug),
  });

  if (query.isLoading) return <PageLoader />;

  if (query.error) {
    if (isPermissionDenied(query.error)) {
      return <NoAccessPanel permission={query.error.permission ?? "documentation:view"} />;
    }
    return <Missing message="We could not load this training module. It may have been unpublished." />;
  }
  if (!query.data) return <Missing message="This training module does not exist." />;

  return <Player key={query.data.slug} mod={query.data} />;
}

function Missing({ message }: { message: string }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 2, justifyItems: "start" }} data-testid="module-missing">
      <BackButton to={TRAININGS_PATH} label="Online Training" />
      <Typography sx={{ color: "text.secondary" }}>{message}</Typography>
    </Box>
  );
}

function Player({ mod }: { mod: ModuleDetail }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [watched, setWatched] = useState<Set<number>>(() => readWatched(mod.slug));
  // True once the viewer pressed play: from then on a new topic autoplays.
  const [started, setStarted] = useState(false);

  const requested = Number(params.get("topic"));
  const fallback = useMemo(() => resumeTopic(mod, readWatched(mod.slug))?.id ?? null, [mod]);
  const pos = positionOf(mod, Number.isFinite(requested) && requested > 0 ? requested : null, fallback);
  const current = pos.current;

  const markWatched = useCallback(
    (id: number) => {
      setWatched((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev).add(id);
        writeWatched(mod.slug, next);
        return next;
      });
    },
    [mod.slug],
  );

  const goTo = useCallback(
    (topic: ModuleTopic | null, opts: { play?: boolean } = {}) => {
      if (!topic) return;
      if (opts.play) setStarted(true);
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.set("topic", String(topic.id));
        return n;
      });
    },
    [setParams],
  );

  const onNext = (): void => {
    if (current) markWatched(current.id);
    goTo(pos.next);
  };

  const done = progressOf([...watched].filter((id) => mod.playlist.includes(id)), pos.total);
  const watchedCount = mod.playlist.filter((id) => watched.has(id)).length;
  const section = current ? sectionOf(mod, current) : null;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }} data-testid="module-player">
      <Box sx={{ display: "grid", gap: 1.5 }}>
        <Box>
          <BackButton to={TRAININGS_PATH} label="Online Training" />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, justifyContent: "space-between" }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {mod.title}
              </Typography>
              <Chip
                size="small"
                label={mod.category === "extension" ? "Extension" : "Service"}
                sx={{ height: 22, fontWeight: 600, bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: "primary.main" }}
              />
            </Box>
            {mod.summary && <Typography sx={{ color: "text.secondary", mt: 0.25 }}>{mod.summary}</Typography>}
          </Box>
          {pos.total > 0 && (
            <Box sx={{ minWidth: 200 }} data-testid="module-progress">
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5, textAlign: "right" }}>
                {watchedCount} of {pos.total} watched
              </Typography>
              <LinearProgress
                variant="determinate"
                value={done * 100}
                aria-label="Module progress"
                sx={{ height: 6, borderRadius: 3, bgcolor: "surfaceSoft", "& .MuiLinearProgress-bar": { borderRadius: 3 } }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {pos.total === 0 ? (
        <Box
          data-testid="module-empty"
          sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 4, textAlign: "center", color: "text.secondary" }}
        >
          This module has no videos yet. Check back soon.
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 3,
            alignItems: "start",
            // The CEO's split: video three quarters, menu one quarter.
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 3fr) minmax(0, 1fr)" },
          }}
        >
          <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
            {current && (
              <Stage
                key={current.id}
                topic={current}
                index={pos.index}
                total={pos.total}
                started={started}
                next={pos.next}
                onStart={() => setStarted(true)}
                onEnded={() => markWatched(current.id)}
                onPlayNext={() => goTo(pos.next, { play: true })}
              />
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => goTo(pos.prev)}
                  disabled={!pos.prev}
                  startIcon={<ChevronLeft size={18} />}
                  data-testid="topic-prev"
                  sx={{ maxWidth: "100%" }}
                >
                  <NavLabel label="Previous" title={pos.prev?.title} />
                </Button>
              </Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }} data-testid="topic-counter">
                {pos.index + 1} / {pos.total}
              </Typography>
              <Box sx={{ justifySelf: "end" }}>
                {pos.next ? (
                  <Button
                    variant="contained"
                    onClick={onNext}
                    endIcon={<ChevronRight size={18} />}
                    data-testid="topic-next"
                    sx={{ maxWidth: "100%" }}
                  >
                    <NavLabel label="Next" title={pos.next.title} />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => current && markWatched(current.id)}
                    disabled={!current || watched.has(current.id)}
                    startIcon={<CheckCircle2 size={18} />}
                    data-testid="topic-finish"
                  >
                    {current && watched.has(current.id) ? "Completed" : "Mark complete"}
                  </Button>
                )}
              </Box>
            </Box>

            {watchedCount === pos.total && (
              <Box
                data-testid="module-complete"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.1),
                  color: "success.main",
                }}
              >
                <PartyPopper size={20} />
                <Typography sx={{ fontWeight: 600 }}>You have watched every video in {mod.title}.</Typography>
              </Box>
            )}

            {current && (
              <Box sx={{ display: "grid", gap: 0.5 }}>
                {section && section.id !== current.id && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.disabled" }}>
                    {section.title}
                  </Typography>
                )}
                <Typography component="h3" sx={{ fontSize: 20, fontWeight: 700 }} data-testid="topic-title">
                  {current.title}
                </Typography>
                {current.summary && (
                  <Typography sx={{ color: "text.secondary", whiteSpace: "pre-line", maxWidth: "76ch" }}>{current.summary}</Typography>
                )}
              </Box>
            )}
          </Box>

          <Menu
            mod={mod}
            currentId={current?.id ?? null}
            watched={watched}
            watchedCount={watchedCount}
            total={pos.total}
            onPick={(t) => goTo(t, { play: started })}
          />
        </Box>
      )}
    </Box>
  );
}

function NavLabel({ label, title }: { label: string; title: string | undefined }): JSX.Element {
  return (
    <Box component="span" sx={{ display: "grid", textAlign: "inherit", minWidth: 0, lineHeight: 1.2 }}>
      <Box component="span">{label}</Box>
      {title && (
        <Box
          component="span"
          sx={{
            display: { xs: "none", sm: "block" },
            fontSize: 11,
            fontWeight: 400,
            opacity: 0.8,
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Box>
      )}
    </Box>
  );
}

function sectionOf(mod: ModuleDetail, topic: ModuleTopic): ModuleSection | null {
  return mod.sections.find((s) => s.id === topic.id || s.id === topic.parent_id) ?? null;
}

// ── The video stage ──────────────────────────────────────────────────────

const STAGE_SX = {
  position: "relative" as const,
  width: "100%",
  aspectRatio: "16 / 9",
  borderRadius: 2,
  overflow: "hidden",
  bgcolor: "#000",
  boxShadow: "0 1px 2px rgba(0,0,0,.08), 0 12px 32px rgba(15,23,42,.18)",
};

function Stage({
  topic,
  index,
  total,
  started,
  next,
  onStart,
  onEnded,
  onPlayNext,
}: {
  topic: ModuleTopic;
  index: number;
  total: number;
  started: boolean;
  next: ModuleTopic | null;
  onStart: () => void;
  onEnded: () => void;
  onPlayNext: () => void;
}): JSX.Element {
  const source = playableSource(topic.video);
  const [ended, setEnded] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const poster = topic.video?.kind === "link" ? topic.video.thumbnail ?? null : null;

  if (source.type === "none") {
    return (
      <Box sx={{ ...STAGE_SX, display: "grid", placeItems: "center" }} data-testid="stage-none">
        <Typography sx={{ color: "common.white", opacity: 0.8 }}>This topic has no video yet.</Typography>
      </Box>
    );
  }

  if (source.type === "external") {
    return (
      <Box sx={{ ...STAGE_SX, display: "grid", placeItems: "center", ...gradient }} data-testid="stage-external">
        <Box sx={{ display: "grid", gap: 1.5, justifyItems: "center", p: 3 }}>
          <Typography sx={{ color: "common.white", textAlign: "center" }}>This video plays on its own page.</Typography>
          <Button variant="contained" color="secondary" href={source.href} target="_blank" rel="noopener noreferrer" endIcon={<ExternalLink size={16} />}>
            Watch the video
          </Button>
        </Box>
      </Box>
    );
  }

  // Not started: the poster and ONE big play button in the middle.
  if (!started) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onStart}
        aria-label={`Play ${topic.title}`}
        data-testid="stage-play"
        sx={{
          ...STAGE_SX,
          display: "block",
          p: 0,
          border: 0,
          cursor: "pointer",
          ...(poster
            ? { backgroundImage: `linear-gradient(rgba(0,0,0,.25), rgba(0,0,0,.55)), url(${poster})`, backgroundSize: "cover", backgroundPosition: "center" }
            : gradient),
          "&:hover .play-disc, &:focus-visible .play-disc": { transform: "translate(-50%, -50%) scale(1.08)" },
        }}
      >
        <Box
          className="play-disc"
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 64, md: 84 },
            height: { xs: 64, md: 84 },
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(255,255,255,.95)",
            color: "primary.main",
            boxShadow: "0 8px 30px rgba(0,0,0,.35)",
            transition: "transform 180ms cubic-bezier(.2,.8,.2,1)",
          }}
        >
          <Play size={34} fill="currentColor" style={{ marginLeft: 4 }} />
        </Box>
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, p: { xs: 2, md: 3 }, textAlign: "left" }}>
          <Typography sx={{ color: "rgba(255,255,255,.75)", fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Topic {index + 1} of {total}
            {formatClock(topic.duration_seconds) ? ` · ${formatClock(topic.duration_seconds)}` : ""}
          </Typography>
          <Typography sx={{ color: "common.white", fontSize: { xs: 18, md: 24 }, fontWeight: 700 }}>{topic.title}</Typography>
        </Box>
      </Box>
    );
  }

  if (source.type === "embed") {
    return (
      <Box sx={STAGE_SX} data-testid="stage-embed">
        <Box
          component="iframe"
          title={topic.title}
          src={withAutoplay(source.src)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={STAGE_SX} data-testid="stage-native">
      <Box
        component="video"
        ref={videoRef}
        controls
        autoPlay
        playsInline
        preload="metadata"
        src={source.src}
        onEnded={() => {
          setEnded(true);
          onEnded();
        }}
        onPlay={() => setEnded(false)}
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
      />
      {ended && (
        <UpNext
          next={next}
          onPlayNext={onPlayNext}
          onReplay={() => {
            setEnded(false);
            const v = videoRef.current;
            if (v) {
              v.currentTime = 0;
              void v.play().catch(() => undefined);
            }
          }}
        />
      )}
    </Box>
  );
}

const gradient = {
  background: "radial-gradient(120% 120% at 0% 0%, #1f3a6b 0%, #0f1e3d 55%, #070f22 100%)",
};

/** The card an ended video shows: the next topic, counting down. */
function UpNext({
  next,
  onPlayNext,
  onReplay,
}: {
  next: ModuleTopic | null;
  onPlayNext: () => void;
  onReplay: () => void;
}): JSX.Element {
  const [left, setLeft] = useState(UP_NEXT_SECONDS);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!next || cancelled) return undefined;
    if (left <= 0) {
      onPlayNext();
      return undefined;
    }
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left, next, cancelled, onPlayNext]);

  return (
    <Box
      data-testid="up-next"
      sx={{
        position: "absolute",
        inset: 0,
        display: "grid",
        placeItems: "center",
        bgcolor: "rgba(4,10,24,.78)",
        backdropFilter: "blur(2px)",
      }}
    >
      <Box sx={{ display: "grid", gap: 1.5, justifyItems: "center", textAlign: "center", p: 3, maxWidth: 420 }}>
        {next ? (
          <>
            <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {cancelled ? "Up next" : `Up next in ${left}`}
            </Typography>
            <Typography sx={{ color: "common.white", fontSize: 20, fontWeight: 700 }}>{next.title}</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Button variant="contained" color="secondary" onClick={onPlayNext} startIcon={<Play size={16} />} data-testid="up-next-play">
                Play next
              </Button>
              {!cancelled && (
                <Button variant="text" onClick={() => setCancelled(true)} sx={{ color: "common.white" }} data-testid="up-next-cancel">
                  Cancel
                </Button>
              )}
              <Button variant="text" onClick={onReplay} startIcon={<RotateCcw size={16} />} sx={{ color: "common.white" }}>
                Replay
              </Button>
            </Box>
          </>
        ) : (
          <>
            <PartyPopper size={28} color="#fff" />
            <Typography sx={{ color: "common.white", fontSize: 20, fontWeight: 700 }}>That was the last video</Typography>
            <Button variant="text" onClick={onReplay} startIcon={<RotateCcw size={16} />} sx={{ color: "common.white" }}>
              Replay
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── The menu ─────────────────────────────────────────────────────────────

function Menu({
  mod,
  currentId,
  watched,
  watchedCount,
  total,
  onPick,
}: {
  mod: ModuleDetail;
  currentId: number | null;
  watched: ReadonlySet<number>;
  watchedCount: number;
  total: number;
  onPick: (t: ModuleTopic) => void;
}): JSX.Element {
  // Collapsed sections, by id. Every section starts open.
  const [closed, setClosed] = useState<Set<number>>(new Set());
  let number = 0;
  const numbers = new Map<number, number>();
  for (const id of mod.playlist) numbers.set(id, ++number);

  return (
    <Box
      component="nav"
      aria-label="Module topics"
      data-testid="module-menu"
      sx={{
        position: { lg: "sticky" },
        top: { lg: 16 },
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        maxHeight: { lg: "calc(100vh - 120px)" },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700 }}>Topics</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {watchedCount}/{total}
        </Typography>
      </Box>
      <Box sx={{ overflowY: "auto" }}>
        {mod.sections.map((section) => {
          const open = !closed.has(section.id);
          const hasLessons = section.lessons.length > 0;
          return (
            <Box key={section.id} sx={{ borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }} data-testid={`menu-section-${section.id}`}>
              {hasLessons ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() =>
                    setClosed((prev) => {
                      const n = new Set(prev);
                      if (n.has(section.id)) n.delete(section.id);
                      else n.add(section.id);
                      return n;
                    })
                  }
                  aria-expanded={open}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    border: 0,
                    bgcolor: "surfaceSoft",
                    color: "text.primary",
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{section.title}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {section.lessons.filter((l) => l.video).length + (section.video ? 1 : 0)} videos
                    </Typography>
                  </Box>
                  <Box sx={{ display: "inline-flex", transition: "transform 160ms", transform: open ? "rotate(0deg)" : "rotate(-90deg)", color: "text.secondary" }}>
                    <ChevronDown size={16} />
                  </Box>
                </Box>
              ) : null}
              {section.video && (
                <TopicRow
                  topic={section}
                  number={numbers.get(section.id)}
                  active={currentId === section.id}
                  watched={watched.has(section.id)}
                  indent={false}
                  onPick={onPick}
                />
              )}
              {!hasLessons && !section.video && (
                <Box sx={{ px: 2, py: 1.25, bgcolor: "surfaceSoft" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{section.title}</Typography>
                </Box>
              )}
              <Collapse in={open} unmountOnExit={false}>
                {section.lessons.map((lesson) => (
                  <TopicRow
                    key={lesson.id}
                    topic={lesson}
                    number={numbers.get(lesson.id)}
                    active={currentId === lesson.id}
                    watched={watched.has(lesson.id)}
                    indent
                    onPick={onPick}
                  />
                ))}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TopicRow({
  topic,
  number,
  active,
  watched,
  indent,
  onPick,
}: {
  topic: ModuleTopic;
  number: number | undefined;
  active: boolean;
  watched: boolean;
  indent: boolean;
  onPick: (t: ModuleTopic) => void;
}): JSX.Element {
  const playable = Boolean(topic.video);
  const clock = formatClock(topic.duration_seconds);
  const Icon = watched ? CheckCircle2 : active ? PlayCircle : Circle;
  return (
    <Box
      component="button"
      type="button"
      disabled={!playable}
      onClick={() => onPick(topic)}
      aria-current={active ? "true" : undefined}
      data-testid={`menu-topic-${topic.id}`}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        pl: indent ? 2.5 : 2,
        pr: 2,
        py: 1.1,
        border: 0,
        borderLeft: 3,
        borderLeftColor: active ? "primary.main" : "transparent",
        bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.08) : "transparent"),
        color: playable ? "text.primary" : "text.disabled",
        cursor: playable ? "pointer" : "default",
        textAlign: "left",
        font: "inherit",
        transition: "background-color 120ms",
        "&:hover:not(:disabled)": { bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.12) : t.palette.action.hover) },
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "inline-flex",
          mt: "1px",
          flexShrink: 0,
          color: watched ? "success.main" : active ? "primary.main" : "text.disabled",
        }}
      >
        <Icon size={16} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: active ? 700 : 500, lineHeight: 1.35 }}>
          {number !== undefined ? `${number}. ` : ""}
          {topic.title}
        </Typography>
        {!playable && <Typography sx={{ fontSize: 11, color: "text.disabled" }}>Coming soon</Typography>}
      </Box>
      {clock && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", fontVariantNumeric: "tabular-nums", mt: "2px" }}>{clock}</Typography>
      )}
      {watched && <Box component="span" sx={visuallyHidden}>(watched)</Box>}
    </Box>
  );
}

const visuallyHidden = {
  position: "absolute" as const,
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap" as const,
};

export default ModulePlayerPage;
